import { Md5 } from 'ts-md5';

import { IJsonTable } from '../model';

export const LOGIN_LOCK_DEFAULT_SECONDS = 15 * 60;

export interface ILoginLockInfo {
  remainingSeconds: number;
  lockUntil?: number;
}

export interface ILoginLockState {
  accountKey: string;
  lockUntil: number;
  remainingSeconds: number;
}

export type LoginAttemptStatus = 'success' | 'error';

export interface ILoginAttemptResult {
  status: LoginAttemptStatus;
  response: IJsonTable;
  lock: ILoginLockInfo | null;
}

/**
 * 将登录账号转换为稳定的缓存标识。
 *
 * @param account 用户输入的登录账号
 * @returns 去除首尾空格并转小写后的 MD5 标识
 * @example createLoginAccountKey(' Demo ') 返回去除空白和小写后的摘要
 */
export function createLoginAccountKey(account: string): string {
  return Md5.hashStr(account.trim().toLowerCase());
}

/**
 * 从登录错误响应中提取后端锁定信息。
 *
 * 后端锁定响应的 data 至少包含 `{ locked: true }`；可选传入
 * `remaining_seconds` 或 `lock_until`。未提供时按需求使用 15 分钟兜底展示。
 *
 * @param response 后端登录响应
 * @param now 当前时间戳，便于单元测试
 * @returns 锁定信息；非锁定响应返回 null
 * @example parseLoginLockInfo({ state: 1001, msg: '', data: { locked: true, remaining_seconds: 872 } })
 */
export function parseLoginLockInfo(response: IJsonTable, now: number = Date.now()): ILoginLockInfo | null {
  if (response.state === 0) return null;

  const data = toRecord(response.data);
  if (!data || data['locked'] !== true) return null;

  const lockUntil = parseLockUntil(data['lock_until'] ?? data['lockUntil']);
  const explicitSeconds = firstPositiveSeconds(
    data['remaining_seconds'],
    data['remainingSeconds'],
    data['retry_after_seconds'],
    data['retryAfterSeconds'],
    data['lock_seconds'],
    data['lockSeconds']
  );
  const untilSeconds = lockUntil === undefined ? undefined : Math.max(1, Math.ceil((lockUntil - now) / 1000));

  return {
    remainingSeconds: explicitSeconds ?? untilSeconds ?? LOGIN_LOCK_DEFAULT_SECONDS,
    lockUntil
  };
}

/**
 * 将剩余秒数格式化为分钟和秒。
 *
 * @param totalSeconds 剩余秒数
 * @returns 两位分钟和两位秒数，格式为 `14:32`
 * @example formatLoginLockClock(872) 返回 `14:32`
 */
export function formatLoginLockClock(totalSeconds: number): string {
  const normalized = normalizeSeconds(totalSeconds);
  const minutes = Math.floor(normalized / 60);
  const seconds = normalized % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * 将剩余秒数格式化为页面提示文案。
 *
 * @param totalSeconds 剩余秒数
 * @returns 中文分钟和秒数
 * @example formatLoginLockRemaining(872) 返回 `14 分 32 秒`
 */
export function formatLoginLockRemaining(totalSeconds: number): string {
  const normalized = normalizeSeconds(totalSeconds);
  const minutes = Math.floor(normalized / 60);
  const seconds = normalized % 60;
  return `${minutes} 分 ${seconds} 秒`;
}

/**
 * 根据锁定截止时间计算当前剩余秒数。
 *
 * @param lockUntil 锁定截止时间戳
 * @param now 当前时间戳
 * @returns 尚未过期时返回正数秒，已过期返回 0
 * @example calculateLoginLockRemaining(Date.now() + 900_000)
 */
export function calculateLoginLockRemaining(lockUntil: number, now: number = Date.now()): number {
  return Math.max(0, Math.ceil((lockUntil - now) / 1000));
}

/**
 * 判断未知值是否为普通对象。
 *
 * @param value 待判断值
 * @returns 是否为可读取属性的对象
 * @example isRecord({ locked: true }) 返回 true
 */
function toRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/**
 * 将后端可能返回的锁定截止时间统一为毫秒时间戳。
 *
 * @param value ISO 时间、秒级时间戳或毫秒时间戳
 * @returns 有效的毫秒时间戳；无法识别时返回 undefined
 * @example parseLockUntil(1793346627) 返回对应毫秒时间戳
 */
function parseLockUntil(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value < 1_000_000_000_000 ? value * 1000 : value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
      return numericValue < 1_000_000_000_000 ? numericValue * 1000 : numericValue;
    }
    const parsedTime = Date.parse(value);
    return Number.isFinite(parsedTime) ? parsedTime : undefined;
  }

  return undefined;
}

/**
 * 从多个候选值中读取第一个正整数秒数。
 *
 * @param values 候选值列表
 * @returns 第一个有效秒数；均无效时返回 undefined
 * @example firstPositiveSeconds(undefined, 872) 返回 872
 */
function firstPositiveSeconds(...values: unknown[]): number | undefined {
  for (const value of values) {
    const numberValue = typeof value === 'string' ? Number(value) : value;
    if (typeof numberValue === 'number' && Number.isFinite(numberValue) && numberValue > 0) {
      return Math.ceil(numberValue);
    }
  }
  return undefined;
}

/**
 * 将任意秒数限制为非负整数。
 *
 * @param value 原始秒数
 * @returns 非负整数秒数
 * @example normalizeSeconds(12.8) 返回 12
 */
function normalizeSeconds(value: number): number {
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}
